import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "../components/Motion";
import { getRelease } from "../lib/api";

const COVER = "/images/iam-cover.jpg";
const LITANY = ["I AM", "THE BODY", "THE BREATH", "THE MIND", "THE WILL", "THE SOUL", "THE POWER", "THE CREATOR"];

export default function DimensionI() {
  const navigate = useNavigate();
  const [release, setRelease] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"], layoutEffect: false });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "DIMENSION I — I AM | Motion & Meaning";
    getRelease("dimension-i-i-am").then(setRelease).catch(() => {});
  }, []);

  return (
    <div className="bg-[#04060b] text-white" data-testid="dimension-i-page">
      {/* ---- Monumental hero ---- */}
      <section ref={heroRef} className="relative h-[100svh] overflow-hidden">
        <div className="di-stars" />
        <motion.div
          className="absolute inset-y-0 right-0 w-full md:w-[62%]"
          style={{
            y,
            maskImage: "radial-gradient(85% 85% at 62% 42%, black 30%, transparent 78%)",
            WebkitMaskImage: "radial-gradient(85% 85% at 62% 42%, black 30%, transparent 78%)",
          }}
        >
          <img src={COVER} alt="Dimension I — I AM" className="w-full h-full object-cover opacity-70" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#04060b] via-transparent to-[#04060b]/60" />

        <motion.div style={{ opacity }} className="relative z-10 h-full flex flex-col justify-end max-w-[1440px] mx-auto px-6 md:px-12 pb-20 md:pb-28">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.2 }}
            className="font-mono text-[9px] tracking-[0.45em] text-gold/60 mb-8"
          >
            MOTION & MEANING PRESENTS
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="font-mono text-[11px] md:text-xs tracking-[0.6em] text-white/50"
          >
            MULTIDIMENSIONAL
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-2xl md:text-4xl font-light tracking-[0.3em] text-silver mt-5"
          >
            DIMENSION I
          </motion.p>
          <div className="overflow-hidden mt-2">
            <motion.h1
              initial={{ y: "105%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1.3, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
              className="di-metal font-serif text-[5.5rem] sm:text-[9rem] lg:text-[13rem] font-light leading-[0.85]"
            >
              I AM
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.5 }}
            className="font-mono text-[11px] tracking-[0.45em] text-white/40 mt-8"
          >
            THE PHYSICAL
          </motion.p>
        </motion.div>
      </section>

      {/* ---- Litany ---- */}
      <section className="relative py-32 md:py-48 overflow-hidden" data-testid="dimension-i-litany">
        <div className="di-stars opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.45em] text-white/35 mb-20">THE COORDINATES OF THE PHYSICAL</p>
          </Reveal>
          <div className="space-y-10 md:space-y-14">
            {LITANY.map((line, i) => (
              <Reveal key={line} delay={i * 0.04}>
                <p
                  className={`font-serif font-light leading-none ${
                    i === 0
                      ? "di-metal text-6xl md:text-8xl"
                      : `text-4xl md:text-6xl text-white/70 ${i % 2 === 1 ? "md:ml-[18%]" : ""}`
                  }`}
                >
                  {line}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Statement (only approved copy — never fallback filler) ---- */}
      {(release?.statement || release?.description) && (
        <section className="relative py-28 md:py-40 bg-[#070a12]">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="max-w-3xl">
              {release?.statement && (
                <Reveal>
                  <p className="font-mono text-[10px] tracking-[0.45em] text-white/35 mb-10">ON EMBODIMENT</p>
                  <p className="font-serif text-3xl md:text-4xl font-light leading-snug text-silver">
                    {release.statement}
                  </p>
                </Reveal>
              )}
              {release?.description && (
                <Reveal delay={0.15}>
                  <p className="text-white/45 leading-relaxed mt-10 max-w-xl">
                    {release.description}
                  </p>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ---- Artwork moment ---- */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <div className="di-stars opacity-40" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal y={70}>
            <div className="relative overflow-hidden max-w-3xl mx-auto aspect-square">
              <motion.img
                src={COVER}
                alt="MULTIDIMENSIONAL: DIMENSION I — I AM official artwork"
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/15" />
            </div>
            <p className="font-mono text-[9px] tracking-[0.35em] text-white/30 text-center mt-6">
              MULTIDIMENSIONAL: DIMENSION I — I AM · OFFICIAL ARTWORK
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---- Closing ---- */}
      <section className="relative py-32 md:py-48 text-center overflow-hidden">
        <div className="mm-vignette" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-serif text-3xl sm:text-4xl lg:text-6xl font-light leading-tight text-silver">
              BEYOND FORM.<br />BEFORE TIME.<br />
              <span className="di-metal">ALWAYS I AM.</span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-wrap items-center justify-center gap-5 mt-16">
              <button
                onClick={() => navigate("/music/dimension-i-i-am")}
                data-testid="dimension-i-enter-album"
                className="bg-[#d9dde4] text-[#05070d] font-mono text-[11px] tracking-[0.25em] px-10 py-4 hover:bg-white transition-colors duration-300"
              >
                ENTER THE ALBUM
              </button>
              <button
                onClick={() => navigate("/store")}
                data-testid="dimension-i-store"
                className="border border-white/25 text-white/80 font-mono text-[11px] tracking-[0.25em] px-10 py-4 hover:border-silver hover:text-silver transition-colors duration-300"
              >
                THE STORE
              </button>
            </div>
            <Link
              to="/"
              data-testid="dimension-i-return"
              className="inline-block mt-14 font-mono text-[10px] tracking-[0.35em] text-white/35 hover:text-gold transition-colors"
            >
              ← RETURN TO THE UNIVERSE
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
